import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2, Calendar, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useListOrganizations } from '@/api/organizations/list-organizations';
import { CreateOrganizationDialog } from '@/components/create-organization-dialog';
import { formatDate } from '@/utils/date';
import { storage } from '@/utils/storage';

export const Route = createFileRoute('/_authenticated/organizations/')({
  component: OrganizationsPage
});

function OrganizationsPage() {
  const navigate = useNavigate();
  const organizationsQuery = useListOrganizations();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  if (organizationsQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-6xl space-y-6 text-muted-foreground">Chargement...</div>
        </div>
      </div>
    );
  }

  if (organizationsQuery.isError) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-6xl space-y-6 text-center">
            <p className="text-muted-foreground">Erreur lors du chargement des organisations.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleOrganizationClick = (organizationId: string) => {
    navigate({ to: '/organizations/$id', params: { id: organizationId } });
  };

  const handleOrganizationCreated = (organizationId: string) => {
    setIsCreateDialogOpen(false);
    storage.setOrganizationId(organizationId);
    navigate({ to: '/organizations/$id', params: { id: organizationId } });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Mes organisations</h1>
              <p className="text-muted-foreground">Gérez vos organisations et accédez à leurs projets et campagnes.</p>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Créer une organisation
            </Button>
          </div>

          {organizationsQuery.data?.organizations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 bg-muted rounded-full w-fit mb-4">
                  <Building2 className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Aucune organisation</h3>
                <p className="text-muted-foreground mb-6 text-center max-w-md">
                  Créez votre première organisation pour commencer à collaborer avec votre équipe.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Créer une organisation
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {organizationsQuery.data?.organizations.map((org) => (
                <Card
                  key={org.id}
                  className="hover:shadow-lg transition-all cursor-pointer hover:border-primary/50"
                  onClick={() => handleOrganizationClick(org.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2 mb-2">
                          <Building2 className="h-5 w-5 text-primary" />
                          {org.name}
                        </CardTitle>
                        {org.description && (
                          <CardDescription className="line-clamp-2">{org.description}</CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Propriétaire: {org.ownerEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>Créé le {formatDate(org.createdOn)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateOrganizationDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onOrganizationCreated={handleOrganizationCreated}
      />
    </div>
  );
}
